// 📋 CONFIGURACIÓN COMPLETA DEL COMERCIO
// Archivo: src/config/comercio.js
// Fecha: Enero 2025
// Descripción: Configuración completa del comercio para el sistema

// 🏪 CONFIGURACIÓN DEL COMERCIO
export const COMERCIO_CONFIG = {
  // ID del comercio (dinámico desde token)
  ID: null,
  
  // Nombre del comercio (dinámico desde token)
  NOMBRE: null,
  
  // Descripción
  DESCRIPCION: 'Sistema de gestión de comercios SinFilas',
  
  // Estado del comercio
  ACTIVO: true,
  
  // Configuración de desarrollo
  DESARROLLO: {
    // Comercio ID dinámico desde token
    COMERCIO_ID: null,
    
    // APIs de desarrollo
    API_BASE_URL: 'https://www.sinfilas.com.ar/sf3/ventas/api',
    
    // Base de datos de desarrollo
    DATABASE: 'sinfilas_qr',
    
    // Logs de desarrollo
    LOG_LEVEL: 'debug'
  },
  
  // Configuración de producción
  PRODUCCION: {
    // Comercio ID dinámico desde token
    COMERCIO_ID: null,
    
    // APIs de producción
    API_BASE_URL: 'https://www.sinfilas.com.ar/sf3/ventas/api',
    
    // Base de datos de producción
    DATABASE: 'sinfilas_prod',
    
    // Logs de producción
    LOG_LEVEL: 'info'
  }
};

// 🔧 FUNCIONES DE CONFIGURACIÓN

/**
 * Obtiene el ID del comercio actual desde token o localStorage
 * @returns {Promise<number>} ID del comercio
 */
// Función removida - el token se maneja centralmente en App.jsx

export const getComercioId = async () => {
  try {
    // Obtener token desde localStorage (manejado centralmente por App.jsx)
    const token = localStorage.getItem('comercio_token') || localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Token no encontrado en la URL ni en localStorage');
    }
    
    // Validar token y obtener comercio_id
    const response = await fetch(`https://www.sinfilas.com.ar/sf3/ventas/api/comercios_perfil_cargar.php?token=${token}`);
    
    const data = await response.json();
    
    if (data.success && data.data && data.data.id) {
      return data.data.id;
    } else {
      console.error('❌ [COMERCIO] Token inválido. Data completa:', JSON.stringify(data, null, 2));
      throw new Error('Token inválido o expirado');
    }
  } catch (error) {
    console.error('❌ [COMERCIO] Error obteniendo comercio_id:', error);
    throw error;
  }
};

/**
 * Obtiene la URL base de la API
 * @returns {string} URL base de la API
 */
export const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return COMERCIO_CONFIG.DESARROLLO.API_BASE_URL;
  }
  
  return COMERCIO_CONFIG.PRODUCCION.API_BASE_URL;
};

/**
 * Obtiene el nivel de logging
 * @returns {string} Nivel de logging
 */
export const getLogLevel = () => {
  if (process.env.NODE_ENV === 'development') {
    return COMERCIO_CONFIG.DESARROLLO.LOG_LEVEL;
  }
  
  return COMERCIO_CONFIG.PRODUCCION.LOG_LEVEL;
};

/**
 * Configura el comercio por parámetro (para futuro multi-comercio)
 * @param {number} comercioId - ID del comercio
 */
export const setComercioId = (comercioId) => {
  if (typeof comercioId === 'number' && comercioId > 0) {
    localStorage.setItem('comercio_id', comercioId.toString());
  } else {
    console.error('❌ ID de comercio inválido:', comercioId);
  }
};

/**
 * Obtiene la configuración completa del comercio
 * @returns {object} Configuración del comercio
 */
export const getComercioConfig = async () => {
  return {
    id: await getComercioId(),
    nombre: COMERCIO_CONFIG.NOMBRE,
    descripcion: COMERCIO_CONFIG.DESCRIPCION,
    activo: COMERCIO_CONFIG.ACTIVO,
    apiBaseUrl: getApiBaseUrl(),
    logLevel: getLogLevel(),
    modo: process.env.NODE_ENV || 'development'
  };
};

/**
 * Obtiene el nombre del comercio actual desde localStorage
 * @returns {string} Nombre del comercio
 */
export const getComercioNombre = async () => {
  try {
    const token = localStorage.getItem('comercio_token') || localStorage.getItem('token');
    
    if (!token) {
      return 'Comercio';
    }
    
    const response = await fetch(`https://www.sinfilas.com.ar/sf3/ventas/api/comercios_perfil_cargar.php?token=${token}`);
    const data = await response.json();
    
    if (data.success && data.data && data.data.fantasia) {
      return data.data.fantasia;
    }
    
    return 'Comercio';
  } catch (error) {
    console.error('❌ [COMERCIO] Error obteniendo nombre:', error);
    return 'Comercio';
  }
};

/**
 * Obtiene la descripción del comercio actual
 * @returns {string} Descripción del comercio
 */
export const getComercioDescripcion = () => {
  return COMERCIO_CONFIG.DESCRIPCION;
};

// 📊 VALIDACIONES

/**
 * Valida que la configuración del comercio sea correcta
 * @returns {boolean} true si la configuración es válida
 */
export const validarConfiguracionComercio = () => {
  // Validar solo elementos síncronos
  const apiBaseUrl = getApiBaseUrl();
  
  if (!apiBaseUrl) {
    console.error('❌ URL de API no configurada');
    return false;
  }
  
  return true;
};

// 🚀 INICIALIZACIÓN

/**
 * Inicializa la configuración del comercio
 */
export const inicializarComercio = () => {
  
  // Validar configuración
  if (!validarConfiguracionComercio()) {
    console.error('❌ Error en la configuración del comercio');
    return false;
  }
  
  // Mostrar información del comercio
  const apiBaseUrl = getApiBaseUrl();
  const logLevel = getLogLevel();
  const modo = process.env.NODE_ENV || 'development';
  
  
  return true;
};

// 📝 CONSTANTES EXPORTADAS

// URL base de la API para uso directo
export const API_BASE_URL = getApiBaseUrl();

// Exportar COMERCIO_ID para compatibilidad
export const COMERCIO_ID = null;

// URLs de las APIs para el comercio actual
export const API_ENDPOINTS = {
  productos: `${API_BASE_URL}/productos_comercio_listar.php`,
  productosEditar: `${API_BASE_URL}/productos_comercio_editar.php`,
  productosEliminar: `${API_BASE_URL}/productos_eliminar.php`,
  productosAr: `${API_BASE_URL}/productos_ar_buscar.php`,
  inventario: `${API_BASE_URL}/inventario_comercio.php`,
  stockBajo: `${API_BASE_URL}/stock_bajo_comercio.php`,
  productosAgotados: `${API_BASE_URL}/productos_agotados_comercio.php`,
  ventas: `${API_BASE_URL}/ventas_comercio.php`,
  ventasCrear: `${API_BASE_URL}/ventas_crear.php`,
  ventasGuardar: `${API_BASE_URL}/ventas_guardar.php`,
  dashboardStats: `${API_BASE_URL}/dashboard_stats.php`,
  uploadImagen: `${API_BASE_URL}/upload_imagen.php`,
  eliminarImagen: `${API_BASE_URL}/eliminar_imagen.php`,
  comercioData: `${API_BASE_URL}/comercios_perfil_cargar.php`,
  // Reportes
  reportesInventario: `${API_BASE_URL}/reportes_inventario_completo.php`,
  reportesVentas: `${API_BASE_URL}/reportes_ventas_periodo.php`,
  reportesFinancieros: `${API_BASE_URL}/reportes_financieros.php`,
  reportesAnalisis: `${API_BASE_URL}/reportes_analisis.php`,
  reportesExportar: `${API_BASE_URL}/reportes_exportar.php`
};


// Configuración por defecto
export default COMERCIO_CONFIG;
